import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useSiteContent } from "@/lib/siteContent";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const CHAMPAGNE = "https://images.unsplash.com/photo-1543418219-44e30b057fea?w=1400&q=85";
const PARIS = "https://images.unsplash.com/photo-1492136344046-866c85e0bf04?w=1400&q=85";
const PROVENCE = "https://images.unsplash.com/photo-1689275232185-957a6dbae87b?w=1400&q=85";
const COUPLE = "https://images.unsplash.com/photo-1756674105285-a136b84d869c?w=1400&q=85";
const HERO_FALLBACK = "https://images.unsplash.com/photo-1724500123062-ca4c00df91e6?w=2000&q=85";

export default function Home() {
  const { t, lang } = useI18n();
  const { blocks } = useSiteContent();
  const [email, setEmail] = useState("");
  const heroImg = blocks["home.hero.image"]?.[lang] || blocks["home.hero.image"]?.fr || HERO_FALLBACK;
  const heroTitle1 = blocks["home.hero.title_1"]?.[lang] || t("home.title_1");
  const heroTitle2 = blocks["home.hero.title_2"]?.[lang] || t("home.title_2");
  const heroLede = blocks["home.hero.lede"]?.[lang] || t("home.lede");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter/subscribe", { email, language: lang });
      toast.success(lang === "fr" ? "Merci ! Vérifiez votre boîte." : "Thank you! Check your inbox.");
      setEmail("");
    } catch {
      toast.error(lang === "fr" ? "Une erreur est survenue." : "Something went wrong.");
    }
  };

  const signatures = [
    { key: "wedding", img: heroImg, to: "/experiences#wedding" },
    { key: "champagne", img: CHAMPAGNE, to: "/experiences#champagne" },
    { key: "private", img: PROVENCE, to: "/experiences#private" },
    { key: "paris", img: PARIS, to: "/experiences#paris" },
    { key: "honeymoon", img: COUPLE, to: "/experiences#honeymoon" },
    { key: "proposal", img: heroImg, to: "/experiences#proposal" },
  ];

  const testimonials = [
    { name: "Emma & James", city: "Manhattan, NY", quote: lang === "fr" ? "Un mariage à Villandry pensé au millimètre. Lucile a rendu l'impossible fluide." : "A Villandry wedding orchestrated to perfection. Lucile made the impossible feel easy." },
    { name: "The Hartwell family", city: "Greenwich, CT", quote: lang === "fr" ? "Nos enfants parlent encore de la Provence. Les adresses, les rencontres — inaccessibles autrement." : "Our kids still talk about Provence. The addresses, the encounters — unreachable otherwise." },
    { name: "David & Anna", city: "Boston, MA", quote: lang === "fr" ? "Une escapade Champagne qui a la mémoire d'un premier voyage." : "A Champagne escape with the memory of a first journey." },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] w-full overflow-hidden -mt-20" data-testid="hero-section">
        <img src={heroImg} alt="French château" className="absolute inset-0 w-full h-full object-cover kenburns" />
        <div className="absolute inset-0 bg-[#0A1128]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/90 via-[#0A1128]/50 to-[#0A1128]/20" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24">
          <div className="max-w-3xl hero-text">
            <div className="overline text-[#D4AF37] fade-up" data-testid="hero-overline">{t("home.overline")}</div>
            <h1 className="font-serif font-light text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95] mt-6 fade-up-delay-1" data-testid="hero-title">
              {heroTitle1}
              <br />
              <span className="italic text-[#F0C85A]">{heroTitle2}</span>
            </h1>
            <p className="mt-8 text-white text-lg md:text-xl font-light max-w-xl fade-up-delay-2" data-testid="hero-lede">
              {heroLede}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-5 fade-up-delay-3">
              <Link to="/book" className="group bg-[#D4AF37] text-[#0A1128] px-8 py-4 text-xs tracking-[0.24em] uppercase hover:bg-white transition-colors duration-500 inline-flex items-center gap-3" data-testid="hero-cta-book">
                {t("home.cta_book")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/experiences" className="text-white/90 text-xs tracking-[0.24em] uppercase border-b border-white/40 pb-1 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-500" data-testid="hero-cta-services">
                {t("home.cta_services")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="signatures-section">
        <div className="grid md:grid-cols-2 gap-12 items-end mb-16">
          <div>
            <div className="overline text-[#D4AF37]">{t("home.section_signature")}</div>
            <h2 className="font-serif text-4xl md:text-6xl font-light text-[#0A1128] mt-4 leading-tight">
              {t("home.sig_lede")}
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {signatures.map((s, i) => (
            <Link
              key={s.key}
              to={s.to}
              className="group hover-scale"
              data-testid={`signature-card-${s.key}`}
              style={{ marginTop: i % 2 === 1 ? "3rem" : "0" }}
            >
              <div className="overflow-hidden">
                <img src={s.img} alt={t(`home.sig_${s.key}`)} className="w-full h-[420px] object-cover" />
              </div>
              <div className="mt-6 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl text-[#0A1128]">{t(`home.sig_${s.key}`)}</h3>
                  <p className="text-sm mt-2 text-[#0A1128]/70 max-w-sm leading-relaxed">{t(`home.sig_${s.key}_d`)}</p>
                </div>
                <ArrowRight size={20} className="text-[#D4AF37] mt-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#0A1128] text-[#F9F8F6] py-24 md:py-32" data-testid="process-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <div className="overline text-[#D4AF37]">{t("home.section_process")}</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light mt-4">{t("home.process_lede")}</h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12">
            {(lang === "fr"
              ? ["Découverte", "Cadrage", "Devis", "Itinéraire", "Réservations", "Carnet de voyage", "Rencontres", "Séjour", "Retour", "Récit"]
              : ["Discovery", "Framing", "Quote", "Itinerary", "Bookings", "Travel book", "Introductions", "Stay", "Return", "Story"]
            ).map((step, i) => (
              <div key={step} className="border-t border-[#D4AF37]/40 pt-5">
                <div className="text-[#D4AF37] font-serif italic text-xl">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-2 text-sm tracking-wide">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="testimonials-section">
        <div className="overline text-[#D4AF37] mb-10">{t("home.section_testimonials")}</div>
        <div className="grid md:grid-cols-3 gap-12">
          {testimonials.map((tt) => (
            <figure key={tt.name} className="border-t border-[#0A1128]/10 pt-8">
              <blockquote className="font-serif text-2xl text-[#0A1128] leading-snug">&ldquo;{tt.quote}&rdquo;</blockquote>
              <figcaption className="mt-6 text-sm text-[#0A1128]/60">
                <span className="text-[#D4AF37]">—</span> {tt.name}, {tt.city}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[#F9F8F6] border-t border-[#0A1128]/10 py-20" data-testid="newsletter-section">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 text-center">
          <div className="overline text-[#D4AF37]">Newsletter</div>
          <h2 className="font-serif text-3xl md:text-5xl font-light text-[#0A1128] mt-4">{t("home.cta_newsletter")}</h2>
          <form onSubmit={subscribe} className="mt-10 flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("home.newsletter_placeholder")}
              className="flex-1 bg-transparent border-b border-[#0A1128]/30 py-3 px-1 focus:outline-none focus:border-[#D4AF37] text-[#0A1128]"
              data-testid="newsletter-email"
            />
            <button type="submit" className="bg-[#0A1128] text-[#F9F8F6] px-8 py-3 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors duration-500" data-testid="newsletter-submit">
              {t("home.newsletter_submit")}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
